import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useConfirm } from 'material-ui-confirm';

import Box from '@mui/material/Box';

import type { PaymentGridColumn } from 'domain/payments/types';
import { useUpdateBeneficiaryMutation, useDeleteBeneficiaryMutation } from 'domain/payments/queries';
import { yieldSuccessNotification } from 'lib/toasts';
import BeneficiaryModal from 'pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal';

import BeneficiaryCard, { BeneficiaryItem } from './BeneficiaryCard';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>;
  beneficiaryHeaders: PaymentGridColumn[];
  onSoftRefresh: () => void;
}

const BeneficiaryDetailCellRenderer: React.FC<Props> = ({ data, beneficiaryHeaders, onSoftRefresh }) => {
  const { formatMessage } = useIntl();
  const confirm = useConfirm();
  const updateBeneficiary = useUpdateBeneficiaryMutation();
  const deleteBeneficiary = useDeleteBeneficiaryMutation();

  const [beneficiaries, setBeneficiaries] = useState<BeneficiaryItem[]>(
    (data.beneficiaries as BeneficiaryItem[]) ?? [],
  );
  const [editingBeneficiary, setEditingBeneficiary] = useState<BeneficiaryItem | null>(null);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await confirm({
          title: formatMessage({ id: 'beneficiary.delete.confirm.title', defaultMessage: 'Delete beneficiary?' }),
          description: formatMessage({
            id: 'beneficiary.delete.confirm.description',
            defaultMessage: 'This action cannot be undone!',
          }),
          confirmationText: formatMessage({ id: 'button.delete', defaultMessage: 'Delete' }),
          cancellationText: formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' }),
          confirmationButtonProps: { color: 'error' },
        });
      } catch {
        return;
      }
      deleteBeneficiary.mutate(
        { path: { id } },
        {
          onSuccess: () => {
            setBeneficiaries((prev) => prev.filter((b) => b.id !== id));
            onSoftRefresh();
            yieldSuccessNotification(
              formatMessage({
                id: 'payment.toasts.beneficiary.delete.success',
                defaultMessage: 'Beneficiary deleted',
              }),
            );
          },
        },
      );
    },
    [confirm, formatMessage, onSoftRefresh, deleteBeneficiary],
  );

  const handleSetDefault = useCallback(
    (id: string) => {
      updateBeneficiary.mutate(
        { path: { id }, body: { isDefault: true } },
        {
          onSuccess: () => {
            setBeneficiaries((prev) => prev.map((b) => ({ ...b, isDefault: b.id === id })));
            onSoftRefresh();
          },
        },
      );
    },
    [updateBeneficiary, onSoftRefresh],
  );

  const handleEditSubmit = useCallback(
    (submitData: Record<string, unknown>): Promise<unknown> => {
      if (!editingBeneficiary) return Promise.resolve();
      const { details, isDefault, source } = submitData as {
        details?: Record<string, string | undefined>;
        isDefault?: boolean;
        source?: 'erp' | 'user';
      };
      return new Promise<void>((resolve, reject) => {
        updateBeneficiary.mutate(
          { path: { id: editingBeneficiary.id }, body: { details, isDefault, source } },
          {
            onSuccess: () => {
              setBeneficiaries((prev) =>
                prev.map((b) => {
                  if (b.id === editingBeneficiary.id) return { ...b, ...(details ?? {}), isDefault: isDefault ?? b.isDefault };
                  return isDefault ? { ...b, isDefault: false } : b;
                }),
              );
              onSoftRefresh();
              resolve();
            },
            // Reject so the modal stays open + preserves input on a failed update.
            onError: (err) => reject(err),
          },
        );
      });
    },
    [updateBeneficiary, editingBeneficiary, onSoftRefresh],
  );

  const handleWheelCapture = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Box onWheelCapture={handleWheelCapture} sx={{ p: 2, bgcolor: 'background.default' }}>
      {beneficiaries.map((ben) => (
        <BeneficiaryCard
          key={ben.id}
          beneficiary={ben}
          headers={beneficiaryHeaders}
          onEdit={() => setEditingBeneficiary(ben)}
          onDelete={() => handleDelete(ben.id)}
          onSetDefault={() => handleSetDefault(ben.id)}
        />
      ))}

      {editingBeneficiary && (
        <BeneficiaryModal
          onClose={() => setEditingBeneficiary(null)}
          onSubmit={handleEditSubmit}
          vendorId={data.vendorId}
          vendorName={data.vendorDisplayName}
          existingAccountsCount={beneficiaries.length}
          beneficiaryId={editingBeneficiary.id}
          isEditable
          isDefault={editingBeneficiary.isDefault}
        />
      )}
    </Box>
  );
};

export default BeneficiaryDetailCellRenderer;
